import { useTranslation } from "react-i18next";
import { Clock } from "lucide-react";

import { formatUyMinor } from "@/lib/formatUyMinor";

export interface QuoteAwardSummaryProps {
  priceMinor: number;
  /** Platform fee the consumer pays on top of priceMinor. Computed server-side. */
  consumerFeeMinor: number;
  /**
   * IVA on the professional's honorarios, when they declared their price
   * excludes it. Rendered as its own row rather than folded into the total,
   * because a breakdown whose lines do not sum to its own total is worse than
   * no breakdown.
   */
  /**
   * The platform's delivery charge, when this job produces a physical
   * document. Its own row for the same reason the IVA line is: the dialog
   * states that the total is final, so every component of it has to be
   * visible or that statement is a promise the checkout breaks.
   */
  deliveryFeeMinor: number;
  /** What awarding this quote actually costs, delivery included. IVA not included. */
  totalMinor: number;
  currency: string;
  /**
   * The professional's own declaration about their prices. Declarative only —
   * it never enters a computation (quote.entity.ts §3e); the total is the
   * final amount charged either way. It is passed in rather than assumed
   * because QuoteBreakdown on the bid row behind this dialog reads the same
   * flag, and the two must not contradict each other about one quote.
   */
  pricesIncludeIva: boolean;
  /** True only when other bids exist to stay available. */
  hasOtherQuotes: boolean;
}

/**
 * What awarding a quote commits the consumer to, as the body of the award
 * confirmation.
 *
 * It was one paragraph of five sentences with the three figures written into
 * the prose, so the number the consumer is actually agreeing to — the total,
 * which is not the number on the bid row they just clicked — had no more
 * weight than the VAT caveat beside it.
 *
 * The figures are a table because that is what they are, and the total is the
 * only thing scaled up: it is the one value that differs from what the row
 * showed. The payment window is a separate block rather than a fourth and
 * fifth sentence, because it is a different kind of statement — a deadline
 * with a consequence, not part of the arithmetic.
 *
 * Deliberately NOT shown: the delivery estimate (already on the row the
 * consumer clicked) and the quote's own itemisation (that is QuoteBreakdown's
 * job, on the row, and repeating it here would make this the second place the
 * same lines are designed).
 */
export function QuoteAwardSummary({
  priceMinor,
  consumerFeeMinor,
  deliveryFeeMinor,
  totalMinor,
  currency,
  pricesIncludeIva,
  hasOtherQuotes,
}: QuoteAwardSummaryProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="space-y-3">
      {/* Same row grammar as QuoteBreakdown — baseline-aligned, muted label
          left, tabular figure right — so the itemisation on the bid row and
          this summary read as one system rather than two designs. */}
      <dl className="rounded-lg border border-border bg-muted/40 p-4">
        <div className="flex items-baseline justify-between gap-4 text-sm">
          <dt className="text-muted-foreground">{t("requests.user.quoteReviewAwardSummaryQuoteIvaIncluded")}</dt>
          <dd className="tabular-nums text-foreground">{formatUyMinor(priceMinor, currency)}</dd>
        </div>

        {/* No IVA row: it is inside the quoted price (spec 76e23408 D6), and
            a separate line would make this summary sum past its own total. */}

        <div className="mt-2 flex items-baseline justify-between gap-4 text-sm">
          <dt className="text-muted-foreground">{t("requests.user.quoteReviewAwardSummaryFee")}</dt>
          <dd className="tabular-nums text-foreground">
            {formatUyMinor(consumerFeeMinor, currency)}
          </dd>
        </div>

        {deliveryFeeMinor > 0 ? (
          <div className="mt-2 flex items-baseline justify-between gap-4 text-sm">
            <dt className="text-muted-foreground">{t("requests.user.quoteDeliveryFee")}</dt>
            <dd className="tabular-nums text-foreground">
              {formatUyMinor(deliveryFeeMinor, currency)}
            </dd>
          </div>
        ) : null}

        <div className="mt-3 flex items-baseline justify-between gap-4 border-t border-border pt-3">
          <dt className="text-sm font-medium text-foreground">
            {t("requests.user.quoteReviewAwardSummaryTotal")}
          </dt>
          <dd className="text-lg font-semibold tabular-nums text-foreground">
            {formatUyMinor(totalMinor, currency)}
          </dd>
        </div>

        {/* Two separate claims, previously collapsed into the single line
            "IVA no incluido" sitting under the total — which reads as "tax
            will be added" and is the one thing that is NOT true. The first
            line answers the question the consumer is actually asking; the
            IVA declaration is the professional's statement about what is
            inside their own price, and is secondary to it. */}
        <p className="mt-3 text-xs text-foreground">
          {t("requests.user.quoteReviewAwardSummaryFinalAmount")}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {pricesIncludeIva
            ? t("requests.user.quoteReviewAwardSummaryIvaIncluded")
            : t("requests.user.quoteReviewAwardSummaryIvaExcluded")}
        </p>
      </dl>

      <div className="flex items-start gap-3 rounded-lg border border-border p-3">
        <Clock className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
        <div className="space-y-1">
          <p className="text-sm text-foreground">
            {t("requests.user.quoteReviewAwardConfirmPaymentNote")}
          </p>
          {/* Only claimed when it is true. Unconditionally promising "the other
              quotes stay available" on a request with a single bid describes
              quotes that do not exist. */}
          {hasOtherQuotes ? (
            <p className="text-xs text-muted-foreground">
              {t("requests.user.quoteReviewAwardOthersRemain")}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
